import React, { useState, useRef, useEffect } from 'react'
import { Camera, Upload, Loader2, X } from 'lucide-react'
import { uploadToCloudinary } from '../utils/cloudinary'

const MAX_FILE_SIZE = 5242880 // 5MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']

interface ImageUploadProps {
  onUpload: (url: string) => void
  currentUrl?: string | null
}

function ImageUpload({ onUpload, currentUrl }: ImageUploadProps) {
  const [uploading, setUploading] = useState(false)
  const [preview, setPreview] = useState<string | null>(currentUrl || null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setPreview(currentUrl || null)
  }, [currentUrl])

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (file.size > MAX_FILE_SIZE) {
      setUploadError('Image must be 5MB or smaller.')
      e.target.value = ''
      return
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      setUploadError('Only JPG, PNG, WebP, and GIF images are allowed.')
      e.target.value = ''
      return
    }

    setUploadError(null)

    const reader = new FileReader()
    reader.onload = () => setPreview(reader.result as string)
    reader.readAsDataURL(file)

    try {
      setUploading(true)
      const data = await uploadToCloudinary(file, 'players')
      setPreview(data.secure_url)
      onUpload(data.secure_url)
    } catch (error) {
      console.error('Error uploading image:', error)
      setUploadError(error instanceof Error ? error.message : 'Upload failed. Check Cloudinary configuration.')
      setPreview(currentUrl || null)
    } finally {
      setUploading(false)
      e.target.value = ''
    }
  }

  const removeImage = () => {
    setPreview(null)
    setUploadError(null)
    onUpload('')
  }

  return (
    <div className="space-y-3">
      <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-2">
        Player Photo
      </label>

      <div className="relative group w-32 h-32">
        <div className={`w-32 h-32 rounded-2xl border-2 border-dashed flex flex-col items-center justify-center overflow-hidden transition-all ${
          preview ? 'border-green-500 bg-green-500/5' : 'border-gray-800 bg-[#0d1117] hover:border-gray-700'
        }`}>
          {preview ? (
            <img src={preview} alt="Preview" className="w-full h-full object-cover" />
          ) : (
            <div className="relative w-full h-full">
              <img src="/placeholder-user.png" alt="Placeholder" className="w-full h-full object-cover opacity-30" />
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <Camera className="text-gray-400 mb-1" size={20} />
                <span className="text-[8px] text-gray-500 font-black uppercase tracking-widest">Upload Photo</span>
              </div>
            </div>
          )}

          {uploading && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
              <Loader2 className="text-green-500 animate-spin" size={24} />
            </div>
          )}

          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept={ALLOWED_TYPES.join(',')}
            className="absolute inset-0 opacity-0 cursor-pointer"
            disabled={uploading}
          />
        </div>

        {preview && !uploading && (
          <button
            onClick={removeImage}
            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center shadow-lg hover:bg-red-600 transition-colors z-10"
          >
            <X size={14} className="text-white" />
          </button>
        )}
      </div>

      {uploadError && (
        <div className="rounded-xl bg-red-900/20 border border-red-700 px-3 py-2 text-xs text-red-200">
          {uploadError}
        </div>
      )}

      <div className="flex gap-2 text-[10px] text-gray-500 italic">
        <Upload size={12} />
        <span>Accepts JPG, PNG, WebP, GIF — up to 5MB.</span>
      </div>
    </div>
  )
}

export default ImageUpload
